// Resource Center Data
// Complementary tools and services that work alongside AI tools

export type SkillLevel = "Beginner" | "Intermediate" | "Advanced";
// Kept in step with toolsData; see the note on PricingType there.
export type PricingType = "Free" | "Paid";

export interface Resource {
  id: string;
  name: string;
  description: string;
  useCases: string[];
  pricing: string;
  pricingTypes: PricingType[];
  website: string;
  skillLevel: SkillLevel;
  bestFor: string;
  emoji: string;
  category: string; // e.g., "Video Hosting", "Design Tools", "Productivity"
  routeLink?: string; // internal route for the resource's dedicated page
  /** See Tool.isPublished in toolsData.ts. Defaults to true when omitted. */
  isPublished?: boolean;
}

export const resources: Resource[] = [
  {
    id: "gohighlevel",
    name: "HighLevel",
    description: "HighLevel, formerly branded GoHighLevel, is an all-in-one CRM, marketing automation, and AI operations platform built for agencies, coaches, and service businesses. Manage your entire client pipeline in one place, from lead capture and nurture sequences to appointment booking, email and SMS campaigns, and reputation management. It now bundles Conversation AI, Voice AI calling agents, payments and invoicing, and courses and communities, replacing a much larger stack of separate tools.",
    useCases: ["CRM & pipeline management", "Email & SMS automation", "Conversation AI & Voice AI", "Payments & invoicing", "Reputation management"],
    pricing: "From $97/month with a 14-day free trial. SMS, email, and AI usage are billed separately.",
    pricingTypes: ["Paid"],
    website: "https://www.gohighlevel.com/?fp_ref=lead31",
    skillLevel: "Intermediate",
    bestFor: "Best for: coaches, consultants, and agency owners who want to consolidate their CRM, email, funnels, and automation into one platform",
    emoji: "⚡",
    category: "CRM & Marketing Automation",
    routeLink: "/resources/gohighlevel",
  },
  {
    // Moved out of the AI tools grid on 25 Aug 2026, Daniel's call. Canva is a
    // design platform with AI bolted on rather than an AI tool in the sense the
    // Atlas / Comet / Manus taxonomy means, so it sat oddly next to ChatGPT and
    // Claude. Its role tags and logo went with the move: resource cards are
    // emoji + name and carry no role. /resources/canva is unchanged, and the
    // page it serves is EcosystemPath reading lib/ecosystemData, which never
    // depended on the tools list.
    id: "canva",
    name: "Canva AI",
    description: "Canva AI is a conversational assistant built into Canva that generates designs, images, copy, presentations, and even simple interactive apps from plain-language prompts, learning your brand and style as you go. It sits on top of Canva's Magic Studio suite, so you never need design skills.",
    useCases: ["Graphic design", "Social media content", "Presentations", "Image generation", "No-code interactive apps"],
    pricing: "Freemium. Free tier with capped AI use, Pro from about $15/mo",
    pricingTypes: ["Free", "Paid"],
    website: "https://www.canva.com/",
    skillLevel: "Beginner",
    bestFor: "Best for: creating polished visual content and designs quickly without design experience",
    emoji: "🎨",
    category: "Design Tools",
    routeLink: "/resources/canva",
  },
  {
    id: "open-video",
    name: "Open.Video",
    description: "Open.Video lets you run a full YouTube-style channel on your own domain. Sync your existing library across in under an hour, keep 100% of your ad revenue with no platform cut, and own your subscriber emails outright. Every watch page is built to rank in search and in AI answer engines.",
    useCases: ["Creator-owned video channel", "100% ad revenue share", "YouTube auto-sync", "First-party subscriber capture", "SEO-ready watch pages"],
    pricing: "Free. No subscription, and no take-rate on your ad revenue.",
    pricingTypes: ["Free"],
    website: "https://open.video/?a_aid=68f0226fc3294",
    skillLevel: "Beginner",
    bestFor: "Best for: creators who want to own their channel, their audience, and 100% of their video revenue",
    emoji: "🎥",
    category: "Video Hosting",
    routeLink: "/resources/open-video",
  },
];

export const PRICING_TYPES: PricingType[] = ["Free", "Paid"];
export const SKILL_LEVELS: SkillLevel[] = ["Beginner", "Intermediate", "Advanced"];
export const RESOURCE_CATEGORIES: string[] = Array.from(new Set(resources.map(r => r.category)));
